import type { Address } from '~~/types/commercetools';

import { ShippingAddressType } from '~~/types/checkout/ShippingAddressType';
import { shippingAddressTypeCookie } from '~~/constants/cookies';
import registerAddressErrors from '~~/helpers/Checkout/registerAddressErrors/registerAddressErrors';
import type { ValidationProvider } from '~~/types/vee-validate';
import { useShipping } from '~/utils/ct';
import { asyncDebounce } from '~~/helpers/lodash/asyncDebounce';
import { truncateAddressFields } from '~~/helpers/address/truncateAddressFields';
import { ADDRESS_TYPE } from '~~/types/AddressType';

const ADDRESS_PERSISTENCE_DEBOUNCE_MS = 500;

const useCheckoutShipping = () => {

  const { validateAddress } = useShippingValidation();
  const { loading: loadingShipping, error: shippingError } = useShipping();
  const {
    cart,
    setShippingDetails: setCartShippingDetails,
  } = useCartExtended();
  const { postEmail, defaultAddress } = useCheckout();
  const { $cookies } = useNuxtApp();
  const { shippingDetails, setShippingDetailsMutation } = useShippingDetailsStore();
  const shippingAddressDetails = computed<Partial<Address>>({
    get: () => shippingDetails.value ?? {},
    set: (value: Partial<Address>) => {

      setShippingDetailsMutation(value);

    },
  });
  const { billingDetails } = useCheckoutBilling();
  const useCheckoutAddressInstance = useCheckoutAddress({
    addressType: 'Shipping',
    scrollToTopOfForm: '#ShippingForm',
    addressDetails: shippingAddressDetails,
  });
  const {
    displayNotification,
    resetNotifications,
    scrollToField,
    setScrollToField,
  } = useCheckoutAddressInstance;
  const {
    isShippingSelected,
    loading: loadingShippingMethods,
    error: errorShippingMethods,
  } = useShippingMethods();
  const { shippingAddressType, setShippingAddressTypeMutation } = useShippingAddressTypeStore();
  const persistedShippingDetails = useState<Partial<Address>>('persistedShippingDetails', () => ({}));
  const anyShippingError = computed<Error | null | string>(() =>
    shippingError.value.load ||
    errorShippingMethods.selectShippingMethod ||
    errorShippingMethods.loadMethods,
  );

  const canMoveForward = computed(
    () =>
      !loadingShipping.value &&
      !loadingShippingMethods.value &&
      !anyShippingError.value &&
      isShippingSelected,
  );

  const areFieldValuesEqual = ({
    field,
    nextAddress,
    previousAddress,
  }: {
    field: keyof Address;
    nextAddress: Partial<Address>;
    previousAddress: Partial<Address>;
  }) => {

    return (nextAddress[field] ?? '') === (previousAddress[field] ?? '');

  };

  const persistShippingAddress = asyncDebounce(async (field?: string) => {

    if (!field || !cart.value) {

      return;

    }

    const typedField = field as keyof Address;

    if (areFieldValuesEqual({
      field: typedField,
      nextAddress: shippingAddressDetails.value,
      previousAddress: persistedShippingDetails.value,
    })) {

      return;

    }

    await setCartShippingDetails(shippingAddressDetails.value);
    persistedShippingDetails.value = { ...shippingAddressDetails.value };

  }, ADDRESS_PERSISTENCE_DEBOUNCE_MS);

  const handleShippingBeforeSubmit = async (
    isValid: boolean,
    fields: Record<string, ValidationProvider> | undefined) => {

    resetNotifications();
    await setShippingFromBillingIfPossible();

    if (!isValid) {

      registerAddressErrors(fields);

    }

    if (shippingDetails.value) {

      await validateAddress(shippingDetails.value);

    }
    postEmail(false);

  };

  const handleShippingAfterSubmit = () => {

    if (!canMoveForward.value) {

      displayNotification();
      throw new Error('Error');

    }

  };

  const changeShippingDetails = (field: keyof typeof shippingDetails.value,
    value: ValueOf<typeof shippingDetails.value>) => {

    setShippingDetailsMutation({
      ...shippingDetails.value,
      [field]: value,
    });

  };

  const setShippingFromBillingIfPossible = () => {

    if (shippingAddressType.value === ShippingAddressType.SAME_AS_BILLING) {

      if (!billingDetails.value) {

        return;

      }
      // Billing accepts longer values than shipping does, so what is reused as the shipping
      // address has to be cut back to what the shipping form would have allowed.
      setShippingDetailsMutation(
        truncateAddressFields({ ...billingDetails.value, id: undefined }, ADDRESS_TYPE.Shipping),
      );

    }

  };

  const resetShippingDetailsForDifferentAddress = () => {

    setShippingDetailsMutation({ ...defaultAddress });

  };

  const setShippingAddressType = async (newValue: ShippingAddressType) => {

    setShippingAddressTypeMutation(newValue);
    $cookies.set(shippingAddressTypeCookie, newValue);
    await setShippingFromBillingIfPossible();

  };

  const load = async () => {

    await setShippingFromBillingIfPossible();
    persistedShippingDetails.value = { ...shippingDetails.value };

  };

  const handleShippingAddressBlur = async (field?: string) => {

    await persistShippingAddress(field);

  };

  return {
    ...useCheckoutAddressInstance,
    handleShippingBeforeSubmit,
    handleShippingAfterSubmit,
    shippingDetails,
    changeShippingDetails,
    handleShippingAddressBlur,
    anyShippingError,
    scrollToField,
    setScrollToField,
    setShippingFromBillingIfPossible,
    resetShippingDetailsForDifferentAddress,
    shippingAddressType,
    setShippingAddressType,
    load,
  };

};

export default useCheckoutShipping;
